import {
  callerListSchema,
  holdMusicSchema,
  hoursAt,
  maxHoldMusicBytes,
  maxPromptBytes,
  openingHoursSchema,
  promptSchema,
  type CallerListSummary,
  type HoldMusicSummary,
  type Me,
  type OpeningHoursInput,
  type OpeningHoursSummary,
  type PromptSummary,
  type RegionSummary,
} from '@smarton-pbx/shared';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { CalendarPlus, Clock, Copy, ListFilter, Mic, MoreHorizontal, Music, Pencil, Plus, Square, Trash2, Upload, Volume2, X } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../../components/ui';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../../components/table';
import { locale, t, tc, tp } from '../../i18n';
import { useLoad, useSubmit } from '../../lib/hooks';
import { SectionTabs } from './SectionTabs';
import { languageNames } from './languages';
import { useRecorder } from './recorder';

const regionsFor = (me: Me, regions: RegionSummary[]) => (me.role === 'TENANT_ADMIN' ? regions : regions.filter((r) => r.id === me.region?.id));
const errorText = (err: unknown) => (err instanceof Error ? err.message : String(err));
const usedBy = (names: string[]) => (names.length ? names.join(', ') : '—');

/** The actions menu of a row. */
function RowMenu({ name, onEdit, onDelete }: { name: string; onEdit: () => void; onDelete: () => void }) {
  return (
    <Menu
      label={t('Actions for {name}', { name })}
      trigger={(props) => (
        <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name })} {...props}>
          <MoreHorizontal size={18} aria-hidden />
        </button>
      )}
    >
      {(close) => (
        <>
          <button type="button" role="menuitem" onClick={() => (close(), onEdit())}>
            <Pencil size={16} aria-hidden /> {t('Edit')}
          </button>
          <button type="button" role="menuitem" className="danger-item" onClick={() => (close(), onDelete())}>
            <Trash2 size={16} aria-hidden /> {t('Delete')}
          </button>
        </>
      )}
    </Menu>
  );
}

// ---- Prompts ----------------------------------------------------------------------

const duration = (ms: number | null) => (ms == null ? '' : `${Math.floor(ms / 60_000)}:${String(Math.round((ms % 60_000) / 1000)).padStart(2, '0')}`);

/** Audio a flow plays: uploaded or recorded in the browser, a version per language. */
/** `open`: the prompt's recordings, as its name opens them. */
function promptColumns(open: (id: string) => void): Column<PromptSummary>[] {
  return [
    {
      key: 'prompt',
      label: t('Prompt'),
      fixed: true,
      cell: (prompt) => (
        <>
          <button type="button" className="link name" onClick={() => open(prompt.id)}>
            {prompt.name}
          </button>
          <div className="detail subtle">{prompt.regionName}</div>
        </>
      ),
      excel: [
        { label: t('Prompt'), value: (prompt) => prompt.name },
        { label: t('Region'), value: (prompt) => prompt.regionName },
      ],
    },
    {
      key: 'recordings',
      label: t('Recordings'),
      cell: (prompt) => (
        <div className="row" style={{ gap: '0.35rem', flexWrap: 'wrap' }}>
          {latestByLanguage(prompt).map((r) => (
            <Pill key={r.language} kind={r.status === 'READY' ? 'success' : r.status === 'FAILED' ? 'danger' : 'warning'}>
              {r.language.toUpperCase()} v{r.version}
              {r.status === 'READY' ? ` · ${duration(r.durationMs)}` : ` · ${r.status === 'FAILED' ? t('failed') : t('converting')}`}
            </Pill>
          ))}
          <button type="button" className="chip-button" onClick={() => open(prompt.id)} data-testid="prompt-recordings">
            {prompt.recordings.length ? t('Recordings') : t('Add a recording')}
          </button>
        </div>
      ),
      value: (prompt) =>
        latestByLanguage(prompt)
          .map((r) => `${r.language.toUpperCase()} v${r.version}${r.status === 'READY' ? ` · ${duration(r.durationMs)}` : ''}`)
          .join(', '),
    },
    { key: 'usedBy', label: t('Used by'), className: 'hide-mobile muted', cell: (prompt) => usedBy(prompt.usedBy), value: (prompt) => prompt.usedBy.join(', ') },
  ];
}

export function PromptsPage({ me }: { me: Me }) {
  const prompts = useLoad(api.listPrompts);
  const regions = useLoad(api.listRegions);
  const [editing, setEditing] = useState<PromptSummary | 'new' | null>(null);
  const [recording, setRecording] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const table = useTable('prompts', promptColumns(setRecording), mayDownload(me.role) ? { name: t('Prompts'), rows: () => prompts.data ?? [] } : null);

  // Recordings are converted in the background: look again until they are.
  const pending = prompts.data?.some((p) => p.recordings.some((r) => r.status === 'PENDING'));
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(prompts.reload, 1500);
    return () => clearTimeout(timer);
  }, [pending, prompts.data, prompts.reload]);

  async function remove(prompt: PromptSummary) {
    if (!confirm(t('Delete the prompt {name} and its recordings?', { name: prompt.name }))) return;
    try {
      await api.deletePrompt(prompt.id);
      prompts.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  const open = prompts.data?.find((p) => p.id === recording) ?? null;
  return (
    <div className="stack">
      <SectionTabs />
      <PageHeader title={t('Prompts')} description={t('What callers hear in your call flows: greetings, menus, closed messages. Upload a file or record one here.')}>
        <button type="button" onClick={() => setEditing('new')} disabled={!regions.data} data-testid="add-prompt">
          <Plus size={16} aria-hidden /> {t('New prompt')}
        </button>
      </PageHeader>
      {(error ?? prompts.error) && <Notice kind="error">{error ?? prompts.error}</Notice>}
      {!prompts.data ? (
        <Loading />
      ) : prompts.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={Volume2} title={t('No prompts yet')}>
            <p>{t('Make one, then upload or record what callers should hear.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {prompts.data.map((prompt) => (
                <tr key={prompt.id} data-testid="prompt-row" {...opensOnClick(() => setRecording(prompt.id))}>
                  <Cells table={table} row={prompt} />
                  <td className="actions">
                    <RowMenu name={prompt.name} onEdit={() => setEditing(prompt)} onDelete={() => void remove(prompt)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('New prompt') : t('Rename the prompt')}>
        {editing && regions.data && (
          <PromptForm
            prompt={editing === 'new' ? null : editing}
            regions={regionsFor(me, regions.data)}
            onCancel={() => setEditing(null)}
            onSaved={(saved) => {
              setEditing(null);
              prompts.reload();
              if (editing === 'new') setRecording(saved.id);
            }}
          />
        )}
      </Dialog>
      <Dialog open={open !== null} onClose={() => setRecording(null)} title={open ? t('{name}: recordings', { name: open.name }) : ''} description={t('The newest ready version of each language plays. Older ones stay until you delete them.')} wide>
        {open && regions.data && <Recordings prompt={open} languages={regions.data.find((r) => r.id === open.regionId)?.languages ?? ['en']} onChanged={prompts.reload} />}
      </Dialog>
    </div>
  );
}

function latestByLanguage(prompt: PromptSummary) {
  const seen = new Set<string>();
  return prompt.recordings.filter((r) => !seen.has(r.language) && seen.add(r.language));
}

function PromptForm({ prompt, regions, onCancel, onSaved }: { prompt: PromptSummary | null; regions: RegionSummary[]; onCancel: () => void; onSaved: (p: PromptSummary) => void }) {
  const [value, setValue] = useState({ name: prompt?.name ?? '', regionId: prompt?.regionId ?? regions[0]?.id ?? '' });
  const form = useSubmit();
  async function submit(event: FormEvent) {
    event.preventDefault();
    const saved = await form.submit(promptSchema, value, (input) => (prompt ? api.updatePrompt(prompt.id, input) : api.createPrompt(input)));
    if (saved) onSaved(saved);
  }
  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <Field label={tc('thing', 'Name')} error={form.fields.name}>
          <input value={value.name} onChange={(e) => setValue({ ...value, name: e.target.value })} autoFocus placeholder={t('Welcome')} data-testid="prompt-name" />
        </Field>
        {!prompt && (
          <Field label={t('Region')} error={form.fields.regionId}>
            <select value={value.regionId} onChange={(e) => setValue({ ...value, regionId: e.target.value })}>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="prompt-save">
          {prompt ? t('Save') : t('Create')}
        </button>
      </div>
    </form>
  );
}

/** A prompt's recordings per language: play, upload, record in the browser, delete a version. */
function Recordings({ prompt, languages, onChanged }: { prompt: PromptSummary; languages: string[]; onChanged: () => void }) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string | null>(null);
  const [recordingFor, setRecordingFor] = useState<string | null>(null);
  const shown = [...new Set([...languages, ...prompt.recordings.map((r) => r.language)])];

  async function send(language: string, audio: Blob) {
    if (audio.size > maxPromptBytes) return setError(t('That file is over 10 MB.'));
    setBusy(language);
    setError(undefined);
    try {
      await api.addPromptRecording(prompt.id, language, audio);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  const recorder = useRecorder(
    (audio) => {
      if (recordingFor) void send(recordingFor, audio);
      setRecordingFor(null);
    },
    (message) => {
      setError(message);
      setRecordingFor(null);
    },
  );

  function record(language: string) {
    setError(undefined);
    setRecordingFor(language);
    void recorder.start();
  }

  async function removeVersion(recordingId: string, version: number) {
    if (!confirm(t('Delete version {n}?', { n: version }))) return;
    try {
      await api.deletePromptRecording(prompt.id, recordingId);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="dialog-body stack">
      {error && <Notice kind="error">{error}</Notice>}
      {shown.map((language) => {
        const versions = prompt.recordings.filter((r) => r.language === language);
        const live = versions.find((r) => r.status === 'READY');
        const isRecording = recorder.recording && recordingFor === language;
        return (
          <section key={language} className="recording-language" data-testid={`prompt-language-${language}`}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <strong>{languageNames[language] ?? language.toUpperCase()}</strong>
              <div className="row">
                <label className={`button secondary small ${busy === language ? 'disabled' : ''}`}>
                  <Upload size={14} aria-hidden /> {t('Upload')}
                  <input
                    type="file"
                    accept="audio/*"
                    hidden
                    disabled={busy !== null}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void send(language, file);
                    }}
                    data-testid={`prompt-upload-${language}`}
                  />
                </label>
                {isRecording ? (
                  <button type="button" className="small danger" onClick={recorder.stop}>
                    <Square size={14} aria-hidden /> {t('Stop · {n} s', { n: recorder.seconds })}
                  </button>
                ) : (
                  <button type="button" className="secondary small" disabled={recorder.recording || busy !== null} onClick={() => record(language)}>
                    <Mic size={14} aria-hidden /> {t('Record')}
                  </button>
                )}
              </div>
            </div>
            {live ? <audio controls preload="none" src={api.promptAudioUrl(prompt.id, live.id)} className="prompt-audio" /> : <p className="subtle">{versions.length ? t('No recording in this language is ready yet.') : t('No recording in this language yet.')}</p>}
            {versions.length > 0 && (
              <ul className="version-list">
                {versions.map((v) => (
                  <li key={v.id}>
                    <span>
                      {t('Version {n}', { n: v.version })}
                      {v === live && <Pill kind="success">{t('Plays')}</Pill>}
                      {v.status === 'PENDING' && <Pill kind="warning">{t('Converting')}</Pill>}
                      {v.status === 'FAILED' && <Pill kind="danger">{t('Couldn’t convert')}</Pill>}
                    </span>
                    <span className="subtle">
                      {new Date(v.createdAt).toLocaleString(locale)} {v.durationMs != null && `· ${duration(v.durationMs)}`}
                    </span>
                    <a href={api.promptAudioUrl(prompt.id, v.id, true)} target="_blank" rel="noreferrer" className="subtle">
                      {t('Original')}
                    </a>
                    <button type="button" className="ghost icon" aria-label={t('Delete version {n}', { n: v.version })} onClick={() => void removeVersion(v.id, v.version)}>
                      <X size={16} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ---- Hold music -------------------------------------------------------------------

/** The company's own music for callers waiting in a queue: tracks played in turn. */
const playTime = (music: HoldMusicSummary) => music.tracks.reduce((sum, t) => sum + (t.durationMs ?? 0), 0);

/** `open`: the set's tracks, as its name opens them. */
function holdMusicColumns(open: (id: string) => void): Column<HoldMusicSummary>[] {
  return [
    {
      key: 'music',
      label: t('Music'),
      fixed: true,
      cell: (music) => (
        <>
          <button type="button" className="link name" onClick={() => open(music.id)}>
            {music.name}
          </button>
          <div className="detail subtle">{music.regionName}</div>
        </>
      ),
      excel: [
        { label: t('Music'), value: (music) => music.name },
        { label: t('Region'), value: (music) => music.regionName },
      ],
    },
    {
      key: 'tracks',
      label: t('Tracks'),
      cell: (music) => (
        <button type="button" className="chip-button" onClick={() => open(music.id)} data-testid="hold-music-tracks">
          {music.tracks.length ? `${music.tracks.length} · ${duration(playTime(music))}` : t('Add tracks')}
        </button>
      ),
      excel: [
        { label: t('Tracks'), value: (music) => music.tracks.length },
        { label: t('Duration'), value: (music) => duration(playTime(music)) },
      ],
    },
    { key: 'queues', label: t('Queues'), className: 'hide-mobile muted', cell: (music) => usedBy(music.usedBy), value: (music) => music.usedBy.join(', ') },
  ];
}

export function HoldMusicPage({ me }: { me: Me }) {
  const list = useLoad(api.listHoldMusic);
  const regions = useLoad(api.listRegions);
  const [editing, setEditing] = useState<HoldMusicSummary | 'new' | null>(null);
  const [tracksOf, setTracksOf] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const table = useTable('hold-music', holdMusicColumns(setTracksOf), mayDownload(me.role) ? { name: t('Hold music'), rows: () => list.data ?? [] } : null);

  const pending = list.data?.some((m) => m.tracks.some((t) => t.status === 'PENDING'));
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(list.reload, 1500);
    return () => clearTimeout(timer);
  }, [pending, list.data, list.reload]);

  async function remove(music: HoldMusicSummary) {
    if (!confirm(t('Delete {name} and its tracks?', { name: music.name }))) return;
    try {
      await api.deleteHoldMusic(music.id);
      list.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  const open = list.data?.find((m) => m.id === tracksOf) ?? null;
  return (
    <div className="stack">
      <SectionTabs />
      <PageHeader title={t('Hold music')} description={t('What callers hear while they wait in a queue. Upload your own tracks, then choose the music in the queue’s settings.')}>
        <button type="button" onClick={() => setEditing('new')} disabled={!regions.data} data-testid="add-hold-music">
          <Plus size={16} aria-hidden /> {t('New music')}
        </button>
      </PageHeader>
      {(error ?? list.error) && <Notice kind="error">{error ?? list.error}</Notice>}
      {!list.data ? (
        <Loading />
      ) : list.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={Music} title={t('The standard hold music plays')}>
            <p>{t('Add your own: make a set, upload one or more tracks, and choose it for a queue.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {list.data.map((music) => (
                <tr key={music.id} data-testid="hold-music-row" {...opensOnClick(() => setTracksOf(music.id))}>
                  <Cells table={table} row={music} />
                  <td className="actions">
                    <RowMenu name={music.name} onEdit={() => setEditing(music)} onDelete={() => void remove(music)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('New hold music') : t('Rename')}>
        {editing && regions.data && (
          <HoldMusicForm
            music={editing === 'new' ? null : editing}
            regions={regionsFor(me, regions.data)}
            onCancel={() => setEditing(null)}
            onSaved={(saved) => {
              setEditing(null);
              list.reload();
              if (editing === 'new') setTracksOf(saved.id);
            }}
          />
        )}
      </Dialog>
      <Dialog open={open !== null} onClose={() => setTracksOf(null)} title={open ? t('{name}: tracks', { name: open.name }) : ''} description={t('They play in this order, again from the start after the last.')} wide>
        {open && <Tracks music={open} onChanged={list.reload} />}
      </Dialog>
    </div>
  );
}

function HoldMusicForm({ music, regions, onCancel, onSaved }: { music: HoldMusicSummary | null; regions: RegionSummary[]; onCancel: () => void; onSaved: (m: HoldMusicSummary) => void }) {
  const [value, setValue] = useState({ name: music?.name ?? '', regionId: music?.regionId ?? regions[0]?.id ?? '' });
  const form = useSubmit();
  async function submit(event: FormEvent) {
    event.preventDefault();
    const saved = await form.submit(holdMusicSchema, value, (input) => (music ? api.updateHoldMusic(music.id, input) : api.createHoldMusic(input)));
    if (saved) onSaved(saved);
  }
  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <Field label={tc('thing', 'Name')} error={form.fields.name}>
          <input value={value.name} onChange={(e) => setValue({ ...value, name: e.target.value })} autoFocus placeholder={t('Calm music')} data-testid="hold-music-name" />
        </Field>
        {!music && (
          <Field label={t('Region')} error={form.fields.regionId}>
            <select value={value.regionId} onChange={(e) => setValue({ ...value, regionId: e.target.value })}>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="hold-music-save">
          {music ? t('Save') : t('Create')}
        </button>
      </div>
    </form>
  );
}

function Tracks({ music, onChanged }: { music: HoldMusicSummary; onChanged: () => void }) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function upload(files: File[]) {
    setBusy(true);
    setError(undefined);
    try {
      for (const file of files) {
        if (file.size > maxHoldMusicBytes) {
          setError(t('{name} is over 20 MB.', { name: file.name }));
          continue;
        }
        await api.addHoldMusicTrack(music.id, file);
      }
      onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(trackId: string, name: string) {
    if (!confirm(t('Remove {name}?', { name }))) return;
    try {
      await api.deleteHoldMusicTrack(music.id, trackId);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="dialog-body stack">
      {error && <Notice kind="error">{error}</Notice>}
      <p className="subtle">
        {t('Phone lines carry narrow audio, so music sounds as on any call. Use music you may play to callers (licensed for it). Each track up to 20 MB.')}
      </p>
      {music.tracks.length === 0 && <p className="subtle">{t('No tracks yet: callers hear the standard music.')}</p>}
      <ol className="track-list">
        {music.tracks.map((track) => (
          <li key={track.id}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <span>
                <strong>{track.name}</strong>
                {track.status === 'READY' ? <span className="subtle"> · {duration(track.durationMs)}</span> : track.status === 'FAILED' ? <Pill kind="danger">{t('Couldn’t convert')}</Pill> : <Pill kind="warning">{t('Converting')}</Pill>}
              </span>
              <button type="button" className="ghost icon" aria-label={t('Remove {name}', { name: track.name })} onClick={() => void remove(track.id, track.name)}>
                <X size={16} aria-hidden />
              </button>
            </div>
            {track.status === 'READY' && <audio controls preload="none" src={api.holdMusicAudioUrl(music.id, track.id)} className="prompt-audio" />}
          </li>
        ))}
      </ol>
      <label className={`button secondary ${busy ? 'disabled' : ''}`} style={{ justifySelf: 'start' }}>
        <Upload size={16} aria-hidden /> {busy ? t('Uploading…') : t('Add tracks')}
        <input
          type="file"
          accept="audio/*"
          multiple
          hidden
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            if (files.length) void upload(files);
          }}
          data-testid="hold-music-upload"
        />
      </label>
    </div>
  );
}

// ---- Opening hours ----------------------------------------------------------------

const DAYS = [t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')];
type Period = { from: string; to: string; kind: string };

/** Easter Sunday (Gregorian), as YYYY-MM-DD. */
function easter(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const plusDays = (date: string, days: number) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

/** Lithuania's public holidays (closed all day). Those always on a Sunday (Mother's and Father's Day) are left out. */
export function lithuanianHolidays(year: number): { from: string; to: string; label: string }[] {
  const day = (md: string, label: string) => ({ from: `${year}-${md}`, to: `${year}-${md}`, label });
  const sunday = easter(year);
  return [
    day('01-01', t('New Year’s Day')),
    day('02-16', t('Restoration of the State')),
    day('03-11', t('Restoration of Independence')),
    { from: sunday, to: plusDays(sunday, 1), label: t('Easter') },
    day('05-01', t('Labour Day')),
    day('06-24', t('Midsummer (Joninės)')),
    day('07-06', t('Statehood Day')),
    day('08-15', t('Assumption Day')),
    day('11-01', t('All Saints’ Day')),
    day('11-02', t('All Souls’ Day')),
    { from: `${year}-12-24`, to: `${year}-12-26`, label: t('Christmas') },
  ];
}

function describeWeek(hours: Pick<OpeningHoursInput, 'weekly'>): string {
  const open = hours.weekly.filter((p) => p.kind.toLowerCase() === 'open');
  if (open.length === 0) return t('Closed every day');
  const byDay = DAYS.map((_, i) => open.filter((p) => p.day === i + 1).map((p) => `${p.from}–${p.to}`).join(', '));
  const parts: string[] = [];
  for (let i = 0; i < 7; ) {
    let j = i;
    while (j + 1 < 7 && byDay[j + 1] === byDay[i]) j++;
    if (byDay[i]) parts.push(`${DAYS[i]}${j > i ? `–${DAYS[j]}` : ''} ${byDay[i]}`);
    i = j + 1;
  }
  return parts.join(' · ');
}

/** Weekly schedules with holidays and closures, read in the region's time zone. */
/** Open, closed or a period's name, right now in the schedule's time zone. */
function nowText(hours: OpeningHoursSummary): { label: string; exit: string } {
  const { exit } = hoursAt(hours, new Date(), hours.timeZone);
  return { exit, label: exit === 'open' ? t('Open') : exit === 'closed' ? t('Closed') : exit.slice('period:'.length) };
}

function hoursColumns(): Column<OpeningHoursSummary>[] {
  return [
    {
      key: 'schedule',
      label: t('Schedule'),
      fixed: true,
      cell: (hours) => (
        <>
          <div className="name">{hours.name}</div>
          <div className="detail subtle">
            {describeWeek(hours)}
            {hours.exceptions.length ? ` · ${tp(hours.exceptions.length, '{n} exception', '{n} exceptions')}` : ''} · {hours.timeZone}
          </div>
        </>
      ),
      excel: [
        { label: t('Schedule'), value: (hours) => hours.name },
        { label: t('Hours'), value: (hours) => describeWeek(hours) },
        { label: t('Time zone'), value: (hours) => hours.timeZone },
      ],
    },
    {
      key: 'now',
      label: t('Now'),
      cell: (hours) => {
        const now = nowText(hours);
        return (
          <Pill kind={now.exit === 'open' ? 'success' : now.exit === 'closed' ? undefined : 'warning'} dot={now.exit === 'open'}>
            {now.label}
          </Pill>
        );
      },
      value: (hours) => nowText(hours).label,
    },
    { key: 'usedBy', label: t('Used by'), className: 'hide-mobile muted', cell: (hours) => usedBy(hours.usedBy), value: (hours) => hours.usedBy.join(', ') },
  ];
}

export function OpeningHoursPage({ me }: { me: Me }) {
  const list = useLoad(api.listOpeningHours);
  const regions = useLoad(api.listRegions);
  const [editing, setEditing] = useState<OpeningHoursSummary | 'new' | null>(null);
  const [error, setError] = useState<string>();
  const table = useTable('opening-hours', hoursColumns(), mayDownload(me.role) ? { name: t('Opening hours'), rows: () => list.data ?? [] } : null);

  async function remove(hours: OpeningHoursSummary) {
    if (!confirm(t('Delete {name}?', { name: hours.name }))) return;
    try {
      await api.deleteOpeningHours(hours.id);
      list.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="stack">
      <SectionTabs />
      <PageHeader title={t('Opening hours')} description={t('Weekly hours, public holidays and one-off closures. A flow’s Opening hours element checks them for each call, in the region’s time zone.')}>
        <button type="button" onClick={() => setEditing('new')} disabled={!regions.data} data-testid="add-hours">
          <Plus size={16} aria-hidden /> {t('New schedule')}
        </button>
      </PageHeader>
      {(error ?? list.error) && <Notice kind="error">{error ?? list.error}</Notice>}
      {!list.data ? (
        <Loading />
      ) : list.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={Clock} title={t('No opening hours yet')} />
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {list.data.map((hours) => (
                <tr key={hours.id} data-testid="hours-row" {...opensOnClick(() => setEditing(hours))}>
                  <Cells table={table} row={hours} />
                  <td className="actions">
                    <RowMenu name={hours.name} onEdit={() => setEditing(hours)} onDelete={() => void remove(hours)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('New opening hours') : editing ? t('Edit {name}', { name: editing.name }) : ''} wide>
        {editing && regions.data && (
          <HoursForm
            hours={editing === 'new' ? null : editing}
            regions={regionsFor(me, regions.data)}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              list.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** Periods of a day: from, to and what they are (open, or a named period such as Lunch). */
function PeriodsEditor({ periods, kinds, onChange, emptyText }: { periods: Period[]; kinds: string[]; onChange: (p: Period[]) => void; emptyText: string }) {
  const listId = useRef(`kinds-${Math.random().toString(36).slice(2)}`).current;
  return (
    <div className="periods">
      {periods.length === 0 && <span className="subtle">{emptyText}</span>}
      {periods.map((p, i) => (
        <div key={i} className="period">
          <input type="time" value={p.from} onChange={(e) => onChange(periods.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))} aria-label={t('From')} />
          <span aria-hidden>–</span>
          <input type="time" value={p.to} onChange={(e) => onChange(periods.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))} aria-label={t('To')} />
          <input className="period-kind" list={listId} value={p.kind} onChange={(e) => onChange(periods.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} aria-label={t('What it is')} />
          <button type="button" className="ghost icon" aria-label={t('Remove')} onClick={() => onChange(periods.filter((_, j) => j !== i))}>
            <X size={16} aria-hidden />
          </button>
        </div>
      ))}
      <datalist id={listId}>
        {['open', ...kinds.filter((k) => k.toLowerCase() !== 'open')].map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
      <button type="button" className="ghost small" onClick={() => onChange([...periods, periods.length ? { from: '12:00', to: '13:00', kind: t('Lunch') } : { from: '08:00', to: '17:00', kind: 'open' }])}>
        <Plus size={14} aria-hidden /> {t('Period')}
      </button>
    </div>
  );
}

function HoursForm({ hours, regions, onCancel, onSaved }: { hours: OpeningHoursSummary | null; regions: RegionSummary[]; onCancel: () => void; onSaved: () => void }) {
  const [value, setValue] = useState<OpeningHoursInput>(() =>
    hours
      ? { name: hours.name, regionId: hours.regionId, weekly: hours.weekly, exceptions: hours.exceptions }
      : { name: '', regionId: regions[0]?.id ?? '', weekly: [1, 2, 3, 4, 5].map((day) => ({ day, from: '08:00', to: '17:00', kind: 'open' })), exceptions: [] },
  );
  const form = useSubmit();
  const region = regions.find((r) => r.id === value.regionId);
  const kinds = [...new Set([...value.weekly, ...value.exceptions.flatMap((e) => e.periods)].map((p) => p.kind))];
  const year = new Date().getFullYear();

  const setDay = (day: number, periods: Period[]) =>
    setValue({ ...value, weekly: [...value.weekly.filter((p) => p.day !== day), ...periods.map((p) => ({ ...p, day }))].sort((a, b) => a.day - b.day || a.from.localeCompare(b.from)) });

  function addHolidays(y: number) {
    const known = new Set(value.exceptions.map((e) => e.from));
    const added = lithuanianHolidays(y).filter((h) => !known.has(h.from)).map((h) => ({ ...h, periods: [] }));
    setValue({ ...value, exceptions: [...value.exceptions, ...added].sort((a, b) => a.from.localeCompare(b.from)) });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const saved = await form.submit(openingHoursSchema, value, (input) => (hours ? api.updateOpeningHours(hours.id, input) : api.createOpeningHours(input)));
    if (saved) onSaved();
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <div className="form-grid">
          <Field label={tc('thing', 'Name')} error={form.fields.name}>
            <input value={value.name} onChange={(e) => setValue({ ...value, name: e.target.value })} autoFocus placeholder={t('Office hours')} data-testid="hours-name" />
          </Field>
          <Field label={t('Region')} hint={region ? t('Read in {timeZone}', { timeZone: region.timeZone }) : undefined} error={form.fields.regionId}>
            <select value={value.regionId} onChange={(e) => setValue({ ...value, regionId: e.target.value })} disabled={Boolean(hours)}>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3 className="section-title">{t('Every week')}</h3>
            <button
              type="button"
              className="ghost small"
              onClick={() => {
                const monday = value.weekly.filter((p) => p.day === 1);
                setValue({ ...value, weekly: [...value.weekly.filter((p) => p.day > 5), ...[1, 2, 3, 4, 5].flatMap((day) => monday.map((p) => ({ ...p, day })))].sort((a, b) => a.day - b.day) });
              }}
            >
              <Copy size={14} aria-hidden /> {t('Monday’s hours to Tue–Fri')}
            </button>
          </div>
          <p className="subtle">{t('Outside these periods it is closed. A named period (Lunch) is its own way out in flows. A period that ends before it starts runs past midnight.')}</p>
          <div className="week">
            {DAYS.map((label, i) => (
              <div key={label} className="week-day">
                <strong>{label}</strong>
                <PeriodsEditor periods={value.weekly.filter((p) => p.day === i + 1)} kinds={kinds} onChange={(p) => setDay(i + 1, p)} emptyText={t('Closed')} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <h3 className="section-title">{t('Holidays and other days')}</h3>
            <div className="row">
              {region?.countryCode === 'LT' && (
                <button type="button" className="ghost small" onClick={() => addHolidays(year)} data-testid="add-lt-holidays">
                  <CalendarPlus size={14} aria-hidden /> {t('Lithuanian holidays {year}', { year })}
                </button>
              )}
              {region?.countryCode === 'LT' && (
                <button type="button" className="ghost small" onClick={() => addHolidays(year + 1)}>
                  {year + 1}
                </button>
              )}
              <button
                type="button"
                className="ghost small"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  setValue({ ...value, exceptions: [...value.exceptions, { from: today, to: today, label: '', periods: [] }] });
                }}
              >
                <Plus size={14} aria-hidden /> {t('Day')}
              </button>
            </div>
          </div>
          {value.exceptions.length === 0 && <p className="subtle">{t('None. Add public holidays and closures; with no periods the day is closed.')}</p>}
          <div className="exceptions">
            {value.exceptions.map((ex, i) => {
              const update = (patch: Partial<typeof ex>) => setValue({ ...value, exceptions: value.exceptions.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
              return (
                <div key={i} className="exception">
                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    <input type="date" value={ex.from} onChange={(e) => update({ from: e.target.value, to: ex.to < e.target.value ? e.target.value : ex.to })} aria-label={t('From')} />
                    <span aria-hidden>–</span>
                    <input type="date" value={ex.to} onChange={(e) => update({ to: e.target.value })} aria-label={t('To')} />
                    <input value={ex.label} onChange={(e) => update({ label: e.target.value })} placeholder={t('e.g. Christmas')} aria-label={t('What it is')} className="grow" />
                    <button type="button" className="ghost icon" aria-label={t('Remove')} onClick={() => setValue({ ...value, exceptions: value.exceptions.filter((_, j) => j !== i) })}>
                      <Trash2 size={16} aria-hidden />
                    </button>
                  </div>
                  <PeriodsEditor periods={ex.periods} kinds={kinds} onChange={(periods) => update({ periods })} emptyText={t('Closed all day')} />
                  {form.fields[`exceptions.${i}.to`] && <span className="field-error">{form.fields[`exceptions.${i}.to`]}</span>}
                </div>
              );
            })}
          </div>
        </div>
        {Object.keys(form.fields).some((k) => k.startsWith('weekly')) && <Notice kind="error">{t('Check the times: use HH:MM, and a name for each period.')}</Notice>}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="hours-save">
          {t('Save')}
        </button>
      </div>
    </form>
  );
}

// ---- Caller lists -----------------------------------------------------------------

/** Named numbers flows branch on: VIPs, blocked callers. */
function callerListColumns(): Column<CallerListSummary>[] {
  return [
    {
      key: 'list',
      label: t('List'),
      fixed: true,
      cell: (list) => (
        <>
          <div className="name">{list.name}</div>
          <div className="detail subtle">{list.regionName}</div>
        </>
      ),
      excel: [
        { label: t('List'), value: (list) => list.name },
        { label: t('Region'), value: (list) => list.regionName },
      ],
    },
    {
      key: 'numbers',
      label: t('Numbers'),
      className: 'tabular',
      cell: (list) => list.numbers.length,
      // The numbers themselves: what someone downloading a list wants.
      value: (list) => list.numbers.join(', '),
    },
    { key: 'usedBy', label: t('Used by'), className: 'hide-mobile muted', cell: (list) => usedBy(list.usedBy), value: (list) => list.usedBy.join(', ') },
  ];
}

export function CallerListsPage({ me }: { me: Me }) {
  const lists = useLoad(api.listCallerLists);
  const regions = useLoad(api.listRegions);
  const [editing, setEditing] = useState<CallerListSummary | 'new' | null>(null);
  const [error, setError] = useState<string>();
  const table = useTable('caller-lists', callerListColumns(), mayDownload(me.role) ? { name: t('Caller lists'), rows: () => lists.data ?? [] } : null);

  async function remove(list: CallerListSummary) {
    if (!confirm(t('Delete the list {name}?', { name: list.name }))) return;
    try {
      await api.deleteCallerList(list.id);
      lists.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="stack">
      <SectionTabs />
      <PageHeader title={t('Caller lists')} description={t('Numbers a flow can recognise, such as VIP customers or blocked callers.')}>
        <button type="button" onClick={() => setEditing('new')} disabled={!regions.data} data-testid="add-list">
          <Plus size={16} aria-hidden /> {t('New list')}
        </button>
      </PageHeader>
      {(error ?? lists.error) && <Notice kind="error">{error ?? lists.error}</Notice>}
      {!lists.data ? (
        <Loading />
      ) : lists.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={ListFilter} title={t('No caller lists yet')} />
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {lists.data.map((list) => (
                <tr key={list.id} {...opensOnClick(() => setEditing(list))}>
                  <Cells table={table} row={list} />
                  <td className="actions">
                    <RowMenu name={list.name} onEdit={() => setEditing(list)} onDelete={() => void remove(list)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('New caller list') : editing ? t('Edit {name}', { name: editing.name }) : ''}>
        {editing && regions.data && (
          <ListForm
            list={editing === 'new' ? null : editing}
            regions={regionsFor(me, regions.data)}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              lists.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function ListForm({ list, regions, onCancel, onSaved }: { list: CallerListSummary | null; regions: RegionSummary[]; onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState(list?.name ?? '');
  const [regionId, setRegionId] = useState(list?.regionId ?? regions[0]?.id ?? '');
  const [text, setText] = useState(list?.numbers.join('\n') ?? '');
  const form = useSubmit();
  const country = (regions.find((r) => r.id === regionId)?.countryCode ?? 'LT') as CountryCode;

  async function submit(event: FormEvent) {
    event.preventDefault();
    // Numbers as people write them (860012345, +370 600 12345): with the country code.
    const numbers = text
      .split(/[\n,;]+/)
      .map((n) => n.trim())
      .filter(Boolean)
      .map((n) => parsePhoneNumberFromString(n, country)?.number ?? n.replace(/[\s()-]/g, ''));
    const saved = await form.submit(callerListSchema, { name, regionId, numbers }, (input) => (list ? api.updateCallerList(list.id, input) : api.createCallerList(input)));
    if (saved) onSaved();
  }

  const numberError = Object.entries(form.fields).find(([k]) => k.startsWith('numbers'));
  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body stack">
        <Field label={tc('thing', 'Name')} error={form.fields.name}>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder={t('VIP customers')} />
        </Field>
        {!list && (
          <Field label={t('Region')}>
            <select value={regionId} onChange={(e) => setRegionId(e.target.value)}>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t('Numbers')} hint={t('One per line')} error={numberError ? t('Line {n}: {error}', { n: Number(numberError[0].split('.')[1]) + 1, error: numberError[1] }) : undefined}>
          <textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={'+370 600 00000\n860011111'} className="tabular" />
        </Field>
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy}>
          {t('Save')}
        </button>
      </div>
    </form>
  );
}
